import { useState, useEffect } from "react";
import { Loader2, Link2, Unlink } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import {
  getYouTubeStatus,
  connectYouTube,
  disconnectYouTube,
} from "../../services/api";

export default function YouTubeConnectionSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    getYouTubeStatus()
      .then((s) => setStatus(s))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("youtube") === "connected") {
      setSuccess("YouTube connected successfully!");
      setTimeout(() => setSuccess(null), 5000);
      window.history.replaceState({}, "", window.location.pathname);
      getYouTubeStatus().then(setStatus).catch(() => {});
    }
  }, []);

  const handleConnect = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const { authUrl } = await connectYouTube();
      window.open(authUrl, "_blank", "width=600,height=700");
    } catch (e) {
      setError(e.message);
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect YouTube? You will no longer be able to post videos.")) return;
    setActionLoading(true);
    setError(null);
    try {
      await disconnectYouTube();
      setStatus({ connected: false });
      setSuccess("YouTube disconnected");
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff0000">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          YouTube
        </h3>
        {status?.connected && (
          <span
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 10,
              background: `${colors.success}18`,
              color: colors.success,
              fontWeight: 600,
            }}
          >
            Connected
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: `${colors.danger}15`,
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: `${colors.success}15`,
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: colors.textSecondary,
            fontSize: 13,
          }}
        >
          <Loader2 size={14} className="spin" />{" "}
          Checking connection…
        </div>
      ) : status?.connected ? (
        <div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {status.channelTitle && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                }}
              >
                <span style={{ color: colors.textSecondary }}>Channel</span>
                <span style={{ color: colors.text }}>{status.channelTitle}</span>
              </div>
            )}
          </div>
          <button
            onClick={handleDisconnect}
            disabled={actionLoading}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: fonts.body,
              background: "transparent",
              color: colors.danger,
              border: `1px solid ${colors.danger}`,
              borderRadius: 6,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {actionLoading ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Unlink size={14} />
            )}
            Disconnect
          </button>
        </div>
      ) : (
        <div>
          <p
            style={{
              fontSize: 13,
              color: colors.textSecondary,
              marginBottom: 12,
            }}
          >
            Connect your YouTube channel to upload and manage videos directly from Insights.
          </p>
          <button
            onClick={handleConnect}
            disabled={actionLoading}
            style={{
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: fonts.body,
              background: "#ff0000",
              color: "#ffffff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {actionLoading ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Link2 size={14} />
            )}
            Connect to YouTube
          </button>
        </div>
      )}
    </div>
  );
}
